/**
 * Per-site email overrides.
 *
 * The platform settings above are a singleton, so silencing one noisy trigger
 * on one site used to mean killing all mail everywhere. This section is the
 * per-site layer beneath them: pause one site, give one site a different
 * per-recipient cap, or turn a single milestone off for one site only.
 *
 * Every control here has an "Inherit" state, which is the default and means
 * *use the platform value* — never "off". The global "Stop all emails" switch
 * stays master: a site cannot un-pause itself past it.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { Building2, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { coreAPI } from '../../../lib/api';
import type { SiteEmailSettings, SiteEmailSettingsUpdate } from '../../../lib/api';
import { useView } from '../../../contexts/ViewContext';
import { NotificationEmailMode, NotificationMilestoneConfig } from './types';

/** Milestone list mirrors the platform matrix above. */
const MILESTONES: { key: string; label: string }[] = [
  { key: 'order_received', label: 'Order received' },
  { key: 'in_process', label: 'In process' },
  { key: 'ready_for_dispatch', label: 'Ready for dispatch' },
  { key: 'dispatched', label: 'Dispatched / delivered' },
  { key: 'on_hold', label: 'On hold' },
  { key: 'promise_date_change', label: 'Promise date change' },
  { key: 'asn_submitted', label: 'Shipment submitted (ASN)' },
];

/** Mirrors the backend DIGEST_CAPABLE set — see the platform matrix. */
const DIGEST_CAPABLE = new Set([
  'order_received', 'in_process', 'ready_for_dispatch',
  'dispatched', 'on_hold', 'promise_date_change',
]);

/** Tri-state select value. 'inherit' maps to null on the wire. */
type TriState = 'inherit' | 'on' | 'off';

const toTriState = (v: boolean | null): TriState =>
  v === null || v === undefined ? 'inherit' : v ? 'on' : 'off';

const fromTriState = (v: TriState): boolean | null =>
  v === 'inherit' ? null : v === 'on';

/** Milestone override modes: 'inherit' clears the site-level entry. */
type MilestoneMode = 'inherit' | NotificationEmailMode;

interface SiteMilestones {
  milestones?: Record<string, NotificationMilestoneConfig>;
}

function readMilestoneMode(
  cfg: Record<string, unknown> | null,
  key: string,
): MilestoneMode {
  const milestones = (cfg as SiteMilestones | null)?.milestones;
  const entry = milestones?.[key];
  if (!entry || entry.email === undefined) return 'inherit';
  return entry.email;
}

/**
 * Apply a milestone override to a site config, dropping the key entirely when
 * set back to 'inherit' so the site stops overriding it (rather than storing a
 * value that merely happens to match the platform's).
 */
function writeMilestoneMode(
  cfg: Record<string, unknown> | null,
  key: string,
  mode: MilestoneMode,
): Record<string, unknown> | null {
  const base = (cfg || {}) as SiteMilestones & Record<string, unknown>;
  const milestones = { ...(base.milestones || {}) };

  if (mode === 'inherit') {
    delete milestones[key];
  } else {
    milestones[key] = { ...(milestones[key] || { in_app: true }), email: mode };
  }

  const next: Record<string, unknown> = { ...base, milestones };
  // No milestone overrides left -> clear the whole config so the row reads as
  // a clean "inherits everything".
  if (Object.keys(milestones).length === 0) {
    delete next['milestones'];
    if (Object.keys(next).length === 0) return null;
  }
  return next;
}

interface SiteEmailSettingsSectionProps {
  /** Platform-level cap, shown as the value each site inherits. */
  platformRateLimit: number;
  /** True when the global kill switch is on — every site is paused regardless. */
  globallyPaused: boolean;
}

export function SiteEmailSettingsSection({
  platformRateLimit,
  globallyPaused,
}: SiteEmailSettingsSectionProps) {
  const [allRows, setAllRows] = useState<SiteEmailSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingSite, setSavingSite] = useState<number | null>(null);

  // Show only the site currently selected in the header switcher, so this tab
  // configures the site the admin is actually looking at. selectedCompanyId is
  // null for a site-restricted admin, hence the fall back to selectedSiteId
  // (the same pairing the dashboard scopes on).
  const { selectedCompanyId, selectedSiteId, selectedCompanyName, selectedSiteName } = useView();
  const activeSiteId = selectedCompanyId ?? selectedSiteId;
  const activeSiteName = selectedCompanyName ?? selectedSiteName;

  // No active selection (a group-level view) means no single site is "the one
  // being viewed" — list every configurable site rather than guessing one.
  const rows = activeSiteId
    ? allRows.filter((r) => r.site === activeSiteId)
    : allRows;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await coreAPI.getSiteEmailSettings();
      setAllRows(res.data || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(`Could not load per-site email settings: ${message}`);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = async (siteId: number, patch: SiteEmailSettingsUpdate) => {
    setSavingSite(siteId);
    try {
      const res = await coreAPI.updateSiteEmailSettings(siteId, patch);
      setAllRows((prev) => prev.map((r) => (r.site === siteId ? res.data : r)));
      toast.success('Site email settings saved');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Could not save: ${message}`);
      // Re-read so the UI never shows an override that didn't persist.
      void load();
    } finally {
      setSavingSite(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading site email settings…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="space-y-3 py-6">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={() => void load()}>Retry</Button>
        </CardContent>
      </Card>
    );
  }

  if (rows.length === 0) {
    // Distinguish "you have no sites" from "the selected site isn't one you
    // can configure" — collapsing them would misreport an access problem as
    // an empty account.
    return (
      <Card>
        <CardContent className="py-8 text-sm text-muted-foreground">
          {activeSiteId && allRows.length > 0
            ? `${activeSiteName || 'The selected site'} is not a site you can configure. Switch site using the selector in the header.`
            : 'No sites available to configure.'}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Per-site email settings
        </CardTitle>
        <CardDescription>
          Settings for the site selected in the header
          {activeSiteName ? <> — <strong>{activeSiteName}</strong></> : null}.
          Every control defaults to <strong>Inherit</strong>, which uses the
          platform setting, so leaving a site untouched changes nothing about
          it. Transactional and security mail (invites, password resets) is
          never affected by anything here.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {globallyPaused && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/5 p-3 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <span>
              <strong>Stop all emails</strong> is currently ON, so every site is
              paused regardless of the settings below. The global switch is
              master — a site cannot un-pause itself past it.
            </span>
          </div>
        )}

        {rows.map((row) => {
          const busy = savingSite === row.site;
          const paused = toTriState(row.emails_paused);
          const joinedOnly = toTriState(row.notify_joined_users_only);
          return (
            <div key={row.site} className="space-y-4 rounded-lg border p-4">
              <div className="flex items-center justify-between gap-3">
                <h4 className="text-sm font-semibold">{row.site_name}</h4>
                {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                {/* Per-site pause */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Email sending</Label>
                  <Select
                    value={paused}
                    disabled={busy}
                    onValueChange={(v) =>
                      void save(row.site, { emails_paused: fromTriState(v as TriState) })
                    }
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inherit">Inherit (platform)</SelectItem>
                      <SelectItem value="off">Active</SelectItem>
                      <SelectItem value="on">Paused</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Paused holds this site&apos;s notification mail; it resumes
                    when set back.
                  </p>
                </div>

                {/* Per-site cap */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Daily cap per recipient</Label>
                  <Input
                    type="number"
                    min={1}
                    disabled={busy}
                    placeholder={`Inherit (${platformRateLimit})`}
                    defaultValue={row.rate_limit_per_day ?? ''}
                    onBlur={(e) => {
                      const raw = e.target.value.trim();
                      const next = raw === '' ? null : Math.max(1, Number(raw) || 1);
                      if (next === (row.rate_limit_per_day ?? null)) return;
                      void save(row.site, { rate_limit_per_day: next });
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    Blank inherits the platform cap of {platformRateLimit}.
                  </p>
                </div>

                {/* Per-site joined-only */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Only email joined users</Label>
                  <Select
                    value={joinedOnly}
                    disabled={busy}
                    onValueChange={(v) =>
                      void save(row.site, {
                        notify_joined_users_only: fromTriState(v as TriState),
                      })
                    }
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inherit">Inherit (platform)</SelectItem>
                      <SelectItem value="on">Joined users only</SelectItem>
                      <SelectItem value="off">All users</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Per-milestone email override */}
              <div className="space-y-2 border-t pt-3">
                <Label className="text-xs">Milestone email overrides</Label>
                <div className="grid gap-2 sm:grid-cols-2">
                  {MILESTONES.map((m) => {
                    const mode = readMilestoneMode(row.notification_config, m.key);
                    return (
                      <div key={m.key} className="grid grid-cols-[1fr_130px] items-center gap-2">
                        <span className="truncate text-sm">{m.label}</span>
                        <Select
                          value={mode}
                          disabled={busy}
                          onValueChange={(v) =>
                            void save(row.site, {
                              notification_config: writeMilestoneMode(
                                row.notification_config, m.key, v as MilestoneMode,
                              ),
                            })
                          }
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="inherit">Inherit</SelectItem>
                            <SelectItem value="off">Off</SelectItem>
                            {DIGEST_CAPABLE.has(m.key) && (
                              <SelectItem value="digest">Digest</SelectItem>
                            )}
                            <SelectItem value="instant">Instant</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  Silences a single trigger for this site only — e.g. set
                  &ldquo;Ready for dispatch&rdquo; to Off here without changing
                  the other sites.
                </p>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
